import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';

import './Contact.scss';
import apiClient from '../../Config/apiClient';
import { useHeaderStyle } from '../../Context/HeaderStyle/HeaderStyleContext';
import { useAnimateOnScroll } from '../../Utils/hooks/useAnimateOnScroll';
import { usePageTitle } from '../../Utils/hooks/usePageTitle';
import contactSectionFrame from '../../Assets/images/contact_us_section_frame.png';
import contactInputBg from '../../Assets/images/contact_us_input_background.png';
import {
  CONTACT_CARDS,
  FORM_HINTS,
  FORM_PLACEHOLDERS,
  HEADQUARTERS,
  HERO_TEXT,
  HOURS_NOTE,
  HOURS_OF_OPERATION,
  SUBMIT_LABEL,
  SUBMIT_LOADING_LABEL,
  SUBMIT_SUCCESS_SUBTITLE,
  SUBMIT_SUCCESS_TITLE,
} from './contactData';

// ─── Validators ───────────────────────────────────────────────────────────────

const VALIDATORS = {
  name: (v) => {
    if (!v.trim()) return FORM_HINTS.name.required;
    if (v.trim().length < 2) return FORM_HINTS.name.minLength;
    if (!/^[a-zA-Z ]+$/.test(v)) return FORM_HINTS.name.pattern;
    return '';
  },
  email: (v) => {
    if (!v.trim()) return FORM_HINTS.email.required;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return FORM_HINTS.email.format;
    return '';
  },
  message: (v) => {
    if (!v.trim()) return FORM_HINTS.message.required;
    if (v.length > 255) return FORM_HINTS.message.maxLength;
    return '';
  },
};

// ─── Form hook ────────────────────────────────────────────────────────────────

const INITIAL_FORM = { name: '', email: '', message: '' };
const INITIAL_TOUCHED = { name: false, email: false, message: false };

function useContactForm() {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [touched, setTouched] = useState(INITIAL_TOUCHED);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const isValid = Object.keys(VALIDATORS).every((f) => !VALIDATORS[f](formData[f]));

  const handleChange = useCallback(
    (field) => (e) => setFormData((prev) => ({ ...prev, [field]: e.target.value })),
    [],
  );

  const handleBlur = useCallback(
    (field) => () => setTouched((prev) => ({ ...prev, [field]: true })),
    [],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, email: true, message: true });
    if (!isValid || submitting) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      await apiClient.post('/api/contact', {
        name: formData.name,
        email: formData.email,
        message: formData.message,
      });
      setSubmitted(true);
      setFormData(INITIAL_FORM);
      setTouched(INITIAL_TOUCHED);
    } catch (err) {
      const serverMessage = err?.response?.data?.message;
      setSubmitError(serverMessage || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getInputClass = (field) => {
    if (!touched[field]) return 'contact-form__input';
    const hasError = !!VALIDATORS[field](formData[field]);
    return `contact-form__input contact-form__input--${hasError ? 'error' : 'valid'}`;
  };

  return {
    formData,
    touched,
    submitting,
    submitted,
    submitError,
    handleChange,
    handleBlur,
    handleSubmit,
    getInputClass,
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const ContactCard = ({ card, frameImage }) => (
  <a
    className="contact-card"
    href={card.href}
    aria-label={`${card.label} ${card.value}`}
    style={{
      backgroundImage: `url(${frameImage})`,
      backgroundSize: '100% 100%',
      backgroundRepeat: 'no-repeat',
    }}
  >
    <img src={card.icon} alt={card.iconAlt} className="contact-card__icon" />
    <div className="contact-card__info">
      <span className="contact-card__label">{card.label}</span>
      <span className="contact-card__value">{card.value}</span>
      <span className="contact-card__action">{card.action}</span>
    </div>
  </a>
);

const FieldHint = ({ field, value, touched: isTouched }) => {
  if (!isTouched) {
    return (
      <span className="contact-form__hint contact-form__hint--idle">
        {FORM_HINTS[field].idle}
      </span>
    );
  }
  const error = VALIDATORS[field](value);
  if (!error) {
    return (
      <span className="contact-form__hint contact-form__hint--success">
        {FORM_HINTS[field].success}
      </span>
    );
  }
  return <span className="contact-form__hint contact-form__hint--error">{error}</span>;
};

const HoursSection = ({ hours, note }) => (
  <div className="contact-hours">
    <h3 className="contact-hours__title">Hours of operation</h3>
    <div className="contact-hours__rows">
      {hours.map((row) => (
        <div key={row.id} className="contact-hours__row">
          <span className="contact-hours__day">{row.day}</span>
          <span
            className={`contact-hours__time${row.isClosed ? ' contact-hours__time--closed' : ''}`}
          >
            {row.hours}
          </span>
        </div>
      ))}
    </div>
    <p className="contact-hours__note">{note}</p>
  </div>
);

const HeadquartersSection = ({ hq }) => (
  <div className="contact-hq">
    <h3 className="contact-hq__title">Headquarters</h3>
    <div className="contact-hq__address">
      <p className="contact-hq__name">{hq.name}</p>
      {hq.lines.map((line, idx) => (
        <p key={idx} className="contact-hq__line">{line}</p>
      ))}
    </div>
  </div>
);

const HeroText = ({ text }) => (
  <div className="contact-hero">
    <h2 className="contact-hero__heading">{text.headingLine1}</h2>
    <h2 className="contact-hero__heading contact-hero__heading--highlight">{text.headingLine2}</h2>
    <p className="contact-hero__desc">{text.description}</p>
  </div>
);

const FormSuccessState = () => (
  <div className="contact-form__success">
    <div className="contact-form__success-check" aria-hidden="true">
      ✓
    </div>
    <h3 className="contact-form__success-title">{SUBMIT_SUCCESS_TITLE}</h3>
    <p className="contact-form__success-subtitle">{SUBMIT_SUCCESS_SUBTITLE}</p>
  </div>
);

const ContactFormFields = ({ formState }) => {
  const {
    formData,
    touched,
    submitting,
    submitError,
    handleChange,
    handleBlur,
    handleSubmit,
    getInputClass,
  } = formState;

  const frameStyle = {
    backgroundImage: `url(${contactInputBg})`,
    backgroundSize: '100% 100%',
    backgroundRepeat: 'no-repeat',
  };

  return (
    <form className="contact-form" onSubmit={handleSubmit} noValidate>
      <div className="contact-form__field">
        <div className="contact-form__input-frame" style={frameStyle}>
          <input
            type="text"
            className={getInputClass('name')}
            placeholder={FORM_PLACEHOLDERS.name}
            value={formData.name}
            onChange={handleChange('name')}
            onBlur={handleBlur('name')}
            aria-label="Name"
            maxLength={50}
          />
        </div>
        <div className="contact-form__hint-area">
          <FieldHint field="name" value={formData.name} touched={touched.name} />
        </div>
      </div>

      <div className="contact-form__field">
        <div className="contact-form__input-frame" style={frameStyle}>
          <input
            type="email"
            className={getInputClass('email')}
            placeholder={FORM_PLACEHOLDERS.email}
            value={formData.email}
            onChange={handleChange('email')}
            onBlur={handleBlur('email')}
            aria-label="Email"
          />
        </div>
        <div className="contact-form__hint-area">
          <FieldHint field="email" value={formData.email} touched={touched.email} />
        </div>
      </div>

      <div className="contact-form__field">
        <div className="contact-form__input-frame" style={frameStyle}>
          <textarea
            className={getInputClass('message')}
            placeholder={FORM_PLACEHOLDERS.message}
            value={formData.message}
            onChange={handleChange('message')}
            onBlur={handleBlur('message')}
            aria-label="Message"
            rows={4}
            maxLength={255}
          />
        </div>
        <div className="contact-form__hint-area">
          <FieldHint field="message" value={formData.message} touched={touched.message} />
        </div>
      </div>

      <div className="contact-form__submit-error-area">
        {submitError && <p className="contact-form__submit-error">{submitError}</p>}
      </div>

      <button
        type="submit"
        className="h-btn contact-form__submit-btn"
        disabled={submitting}
      >
        {submitting ? SUBMIT_LOADING_LABEL : SUBMIT_LABEL}
      </button>
    </form>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const Contact = () => {
  usePageTitle('Contact Us');
  const { setVariant } = useHeaderStyle();
  const cardsRef = useAnimateOnScroll();
  const leftRef = useAnimateOnScroll();
  const rightRef = useAnimateOnScroll();
  const formState = useContactForm();

  // useLayoutEffect (not useEffect) so the header variant is set BEFORE the
  // first paint on refresh — otherwise the header flashes through the
  // default-variant styling before switching to solid.
  useLayoutEffect(() => {
    setVariant('solid');
    return () => setVariant('default');
  }, [setVariant]);

  return (
    <main className="contact-page" data-testid="contact-page">
      <section className="contact-section">
        <div className="container contact-container">
          {/* Contact Cards Row */}
          <div ref={cardsRef} className="contact-cards slide-up">
            {CONTACT_CARDS.map((card) => (
              <ContactCard key={card.id} card={card} frameImage={contactSectionFrame} />
            ))}
          </div>

          {/* Main 2-Column Grid */}
          <div className="contact-grid">
            {/* Left Column */}
            <div ref={leftRef} className="contact-col-left left-in">
              <div className="contact-left-box">
                <HoursSection hours={HOURS_OF_OPERATION} note={HOURS_NOTE} />
              </div>
              <HeadquartersSection hq={HEADQUARTERS} />
            </div>

            {/* Right Column */}
            <div ref={rightRef} className="contact-col-right right-in">
              <HeroText text={HERO_TEXT} />
              {formState.submitted ? (
                <FormSuccessState />
              ) : (
                <ContactFormFields formState={formState} />
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
